/**
 * @fileoverview TransactionForm Component
 *
 * Handles transaction input form for all transaction types (Expense, Income, Transfer, Adjustment).
 * Manages form state, validation, and category inputs with transaction-type-specific category filtering.
 *
 * @description
 * Provides unified form interface for all transaction types with:
 * - Type-specific category filtering (Expense, Transfer, Income, Adjustment)
 * - Template-based income allocation support
 * - Multi-category row management
 * - Form validation and error display
 *
 * Category filtering:
 * - Expense: Only categories with allowExpense=true (excluding user categories)
 * - Transfer: Only categories with allowTransfer=true
 * - Income/Adjustment: All active categories (unfiltered)
 *
 * @module components/TransactionForm/TransactionForm
 *
 * @param {Object} props - Component props
 * @param {string} props.transactionType - Current transaction type
 * @param {string} props.amount - Transaction amount
 * @param {string} props.description - Transaction description
 * @param {string} props.date - Transaction date
 * @param {Array} props.transactionCategories - Category assignments
 * @param {string} props.currentUser - Selected user
 * @param {boolean} props.paidDirectly - Whether paid directly from joint account
 * @param {boolean} props.useTemplateForIncome - Whether using template for income
 * @param {string} props.adjustmentFromCategory - From category for adjustments
 * @param {string} props.adjustmentToCategory - To category for adjustments
 * @param {Array} props.categories - Available categories
 * @param {Array} props.users - Available users
 * @param {string} props.budgetStartMonth - Budget start month for validation
 * @param {Object} props.errors - Validation errors
 * @param {Function} props.onFieldChange - Called when form fields change
 * @param {Function} props.onCategoryChange - Called when categories change
 * @param {Function} props.onSubmit - Called when form is submitted
 * @param {Function} props.onCancel - Called when form is cancelled
 * @param {Function} props.getAvailableCategories - Gets available categories for selection
 * @param {Function} props.getAvailableCategoriesForExpenses - Gets categories with allowExpense=true
 * @param {Function} props.getAvailableCategoriesForTransfers - Gets categories with allowTransfer=true
 * @param {Function} props.handlePositiveNumberInput - Handles positive number validation
 * @param {Function} props.clearError - Clears field errors
 * @param {Function} props.clearCategoryError - Clears category-specific errors
 * @param {boolean} props.hideSubmitButton - Optional - if true, hides the submit button and errors
 * @param {boolean} props.singleBankMode - Whether budget is in single bank mode
 */
import FormHeader from './FormHeader';
import BasicFieldsRow from './BasicFieldsRow';
import AdjustmentFields from './AdjustmentFields';
import CategoryList from './CategoryList';
import ErrorMessages from './ErrorMessages';
import styles from './TransactionForm.module.css';

function TransactionForm({
    transactionType,
    amount,
    description,
    date,
    transactionCategories,
    currentUserKey,
    paidDirectly,
    useTemplateForIncome,
    adjustmentFromCategory,
    adjustmentToCategory,
    categories,
    _regularCategories,
    users,
    budgetStartMonth,
    errors,
    onFieldChange,
    onCategoryChange,
    onSubmit,
    onCancel,
    getAvailableCategories,
    getAvailableCategoriesForExpenses,
    getAvailableCategoriesForTransfers,
    isGrandfatheredCategory,
    handlePositiveNumberInput,
    clearError,
    clearCategoryError,
    hideSubmitButton = false,
    singleBankMode = false,
    isEdit = false,
    hasTemplateForTransactionMonth = true
}) {

    const handleSubmit = () => {
        onSubmit();
    };

    const handleCancel = () => {
        onCancel();
    };

    return (
        <div>
            {/* Form Header */}
            <FormHeader
                transactionType={transactionType}
                useTemplateForIncome={useTemplateForIncome}
                onCancel={handleCancel}
            />

            {/* General Error Display */}
            {errors.general && (
                <div className={styles.generalError}>
                    ⚠️ {errors.general}
                </div>
            )}

            {/* Main Form Fields */}
            <div className={styles.formFields}>
                <BasicFieldsRow
                    transactionType={transactionType}
                    amount={amount}
                    description={description}
                    date={date}
                    currentUserKey={currentUserKey}
                    paidDirectly={paidDirectly}
                    users={users}
                    budgetStartMonth={budgetStartMonth}
                    errors={errors}
                    onFieldChange={onFieldChange}
                    onCategoryChange={onCategoryChange}
                    handlePositiveNumberInput={handlePositiveNumberInput}
                    clearError={clearError}
                    useTemplateForIncome={useTemplateForIncome}
                    transactionCategories={transactionCategories}
                    singleBankMode={singleBankMode}
                    hasTemplateForTransactionMonth={hasTemplateForTransactionMonth}
                />

                {/* Adjustment-specific form */}
                {transactionType === 'Adjustment' && (
                    <AdjustmentFields
                        adjustmentFromCategory={adjustmentFromCategory}
                        adjustmentToCategory={adjustmentToCategory}
                        categories={categories}
                        errors={errors}
                        onFieldChange={onFieldChange}
                        clearError={clearError}
                    />
                )}

                {/* Regular categories section for non-Adjustment transactions */}
                {transactionType !== 'Adjustment' && (
                    <CategoryList
                        transactionType={transactionType}
                        amount={amount}
                        transactionCategories={transactionCategories}
                        errors={errors}
                        onCategoryChange={onCategoryChange}
                        clearCategoryError={clearCategoryError}
                        getAvailableCategories={getAvailableCategories}
                        getAvailableCategoriesForExpenses={getAvailableCategoriesForExpenses}
                        getAvailableCategoriesForTransfers={getAvailableCategoriesForTransfers}
                        isGrandfatheredCategory={isGrandfatheredCategory}
                        isEdit={isEdit}
                    />
                )}

                {/* Submit Button - only show if not in modal */}
                {!hideSubmitButton && (
                    <>
                        <button
                            onClick={handleSubmit}
                            className={styles.submitButton}
                        >
                            Add {transactionType}
                        </button>

                        {/* Error Messages */}
                        <ErrorMessages errors={errors} />
                    </>
                )}
            </div>
        </div>
    );
}

export default TransactionForm;
